import get from 'lodash/get';
import { PAGE_NAME } from '../../utils/constants';
import { DEFAULT_SECURITY_COMPANY_FILTER } from '../../utils/constants/securityCompany';
import { serviceTypes } from 'utils/constants/serviceTypes';
import uniq from 'lodash/uniq';
import { themeNames } from 'theme/base';
import { isEmpty } from 'lodash';
import { createSelector } from '@reduxjs/toolkit';
import staticUserRoles from 'static/user_roles.json';
import { t } from 'i18next';
// Actions
export const FETCH_SECURITY_COMPANY_SERVICES =
  'FETCH_SECURITY_COMPANY_SERVICES';
export const SET_SECURITY_COMPANY_SERVICES = 'SET_SECURITY_COMPANY_SERVICES';
export const FETCH_SECURITY_COMPANY_DETAIL = 'FETCH_SECURITY_COMPANY_DETAIL';
export const SET_SECURITY_COMPANY_DETAIL = 'SET_SECURITY_COMPANY_DETAIL';
export const SET_SECURITY_COMPANY_BILLING_INFO =
  'SET_SECURITY_COMPANY_BILLING_INFO ';
export const UPDATE_SECURITY_COMPANY = 'UPDATE_SECURITY_COMPANY';
export const FETCH_VIEW_SECURITY_COMPANY_LIST =
  'FETCH_VIEW_SECURITY_COMPANY_LIST';
export const SET_VIEW_SECURITY_COMPANY_LIST = 'SET_VIEW_SECURITY_COMPANY_LIST';
export const SET_VIEW_SECURITY_COMPANY_FILTER =
  'SET_VIEW_SECURITY_COMPANY_FILTER';
export const FETCH_BILLING_INFO = 'FETCH_BILLING_INFO';
export const SET_SELECTED_LANGUAGE = 'SET_SELECTED_LANGUAGE';
export const SET_REFRESH_BILLING_INFO = 'SET_REFRESH_BILLING_INFO';
export const SET_VIEW_SECURITY_COMPANY_FILTER_KEY =
  'SET_VIEW_SECURITY_COMPANY_FILTER_KEY';

const initialState = {
  services: [],
  billingInfo: {},
  views: {
    [PAGE_NAME.REGION]: {
      list: [],
      filters: DEFAULT_SECURITY_COMPANY_FILTER,
      securityCompanyFilterKey: false,
    },
  },
  forceRefresh: false,
};

export default function reducer(state = initialState, action = {}) {
  switch (action.type) {
    case SET_SECURITY_COMPANY_SERVICES:
      return { ...state, ...action.data };
    case SET_SECURITY_COMPANY_DETAIL:
      return { ...state, ...action.details };
    case SET_REFRESH_BILLING_INFO:
      return { ...state, forceRefresh: action.data };
    case SET_SECURITY_COMPANY_BILLING_INFO:
      return {
        ...state,
        billingInfo: action.data,
      };
    case SET_VIEW_SECURITY_COMPANY_LIST: {
      return {
        ...state,
        views: {
          ...state.views,
          [action.location]: {
            ...state.views[action.location],
            list: action.list,
            totalEntries: action.totalEntries,
          },
        },
      };
    }
    case SET_VIEW_SECURITY_COMPANY_FILTER_KEY:
      return {
        ...state,
        views: {
          ...state.views,
          [action.location]: {
            ...state.views[action.location],
            securityCompanyFilterKey: action.key,
          },
        },
      };

    case SET_VIEW_SECURITY_COMPANY_FILTER:
      return {
        ...state,
        views: {
          ...state.views,
          [action.location]: {
            ...state.views[action.location],
            filters: { ...action.filters },
          },
        },
      };
    default:
      return state;
  }
}

export function fetchSecurityCompanyServices(id) {
  return { type: FETCH_SECURITY_COMPANY_SERVICES, id };
}

export function setSecurityCompanyServices(data) {
  return { type: SET_SECURITY_COMPANY_SERVICES, data };
}
export function setSecurityCompanyDetail(details) {
  return { type: SET_SECURITY_COMPANY_DETAIL, details };
}
export function fetchSecurityCompanyDetail(id) {
  return { type: FETCH_SECURITY_COMPANY_DETAIL, id };
}
export function setSecurityBillingInfo(data) {
  return { type: SET_SECURITY_COMPANY_BILLING_INFO, data };
}

export function setRefreshBillingInfo(value) {
  return { type: SET_REFRESH_BILLING_INFO, value };
}

export function fetchBillingInfo() {
  return { type: FETCH_BILLING_INFO };
}
export function updateSecurityCompany(id, securityCompany, toggleLocation) {
  return { type: UPDATE_SECURITY_COMPANY, id, securityCompany, toggleLocation };
}

export function fetchViewSecurityCompanyList({
  location = PAGE_NAME.REGION,
  filters = initialState.views[location].filters,
}) {
  return {
    type: FETCH_VIEW_SECURITY_COMPANY_LIST,
    filters,
    location,
  };
}
export function setViewSecurityCompanyList({
  list,
  location,
  totalEntries = 0,
}) {
  return {
    type: SET_VIEW_SECURITY_COMPANY_LIST,
    list,
    location,
    totalEntries,
  };
}

export function setSelectedLanguage(languageOption) {
  return { type: SET_SELECTED_LANGUAGE, languageOption };
}

export function setViewSecurityCompanyFilter({ filters, location }) {
  return { type: SET_VIEW_SECURITY_COMPANY_FILTER, filters, location };
}
export function setViewSecurityCompanyFilterkey({ key, location }) {
  return { type: SET_VIEW_SECURITY_COMPANY_FILTER_KEY, key, location };
}

// selectors
export const includeService = (state, pattern) => {
  return get(state, 'securityCompany.services', []).some(item =>
    pattern.test(item),
  );
};
export const getSecurityCompanyDetail = state => state.securityCompany;
export const getAreaOfOperation = state => {
  const companyDetails = getSecurityCompanyDetail(state);
  if (companyDetails) {
    return companyDetails.areaOfOperation;
  }
  return {};
};
export const getSecurityCompanyBillingInfo = state =>
  state.securityCompany.billingInfo;
/**
 * Helper function to extract the available services in a company.
 * @param {*} state - whole state of the app
 * @returns {[]} - list of dynamic services that get toggled depending on what service is allowed.
 */
export const getSecurityCompanyServices = state =>
  state.securityCompany.services;
export const getCompanyAssetCategories = state =>
  state.securityCompany.allowableCategories;
export const getCompanyThemes = state => {
  const companyThemes = state?.securityCompany.themes;
  // push the free dark theme const
  if (!isEmpty(companyThemes) && !companyThemes.includes(themeNames.DARK.id)) {
    companyThemes.push(themeNames.DARK.id);
  }
  return companyThemes;
};
export const getRefreshBillingInfo = state =>
  state.securityCompany.forceRefresh;
export const getViewSecurityCompanyList = (state, location) =>
  state.securityCompany.views[location].list;
export const getViewSecurityCompanyFilters = (state, location) =>
  state.securityCompany.views[location].filters;
export const getViewSecurityCompanyTotalEntries = (state, location) =>
  state.securityCompany.views[location].totalEntries;
export const getSecurityCompanyId = state => state.securityCompany.id;
export const getSitesEnabled = state => state.securityCompany.enableSites;
export const getSecurityCompanyFeatures = state =>
  state.securityCompany.features;
/**
 * Helper function to extract the available roles from a company.
 * @param {*} state - the state that contains the company object
 * @returns {[]} - list of roles in the company object
 */
export const getSecurityCompanyRoles = state => state?.securityCompany.roles;

export const checkIfSitePatrolIsEnabled = state =>
  getSecurityCompanyServices(state) &&
  getSecurityCompanyServices(state).includes(serviceTypes.patrol.singleSite);
export const getLanguages = state =>
  state.securityCompany?.availableLanguages || [];
export const totalMediafilesAllowed = () => {
  //TO-DO this will be implemented in the future when api returns number
  // of image and audio files allowed as per the subscription
  // const subscriptions = getSecurityCompanyServices(state)
  return 10;
};

/**
 * Helper function to extract the available services from subscription model.
 * @param {*} state - whole state of the app
 * @returns {[]} - list of fixed services that forms a part of the subscription model. This is used while editing the services.
 */
export const getSecurityCompanyAllowableServices = state =>
  state?.securityCompany?.subscriptions.reduce((prev, curr) => {
    const { allowableServices } = curr;
    prev.push(...allowableServices);
    return uniq(prev);
  }, []);

export const getSecurityCompanySubscriptions = state =>
  state?.securityCompany?.subscriptions;

export const getSecurityCompanySubscriptionsByID = (state, ID) => {
  const allSubscriptions = getSecurityCompanySubscriptions(state);
  return allSubscriptions.filter(sub => sub.planId === ID);
};
const securityCompanystateSelector = state => state.securityCompany;

export const getAllowableRoleServicesMap = createSelector(
  securityCompanystateSelector,
  ({ services, roles }) => {
    if (isEmpty(roles)) return [];
    if (!services || services.length === 0) return []; //there are no services so just return.
    const serviceRoleMap = staticUserRoles.reduce((prev, curr) => {
      if (roles.includes(curr.id)) {
        // if the role exists in static user json
        const [role] = prev.filter(f => f?.role === curr.id);
        const allowableServices = curr.allowableServices
          .filter(service => services.includes(service))
          .map(m => ({
            id: m,
            name: t(m),
          }));
        if (role) {
          prev.allowableServices.push(allowableServices);
        } else {
          prev.push({
            role: { id: curr.id, name: t(curr.id), icon: curr?.icon },
            allowableServices: [...allowableServices],
            canBeTeamLead: curr?.canBeTeamLead,
            multiRole: curr?.multiRole,
          });
        }
      }
      return prev;
    }, []);

    return serviceRoleMap;
  },
);

export const allowableRolesTodisplay = createSelector(
  getAllowableRoleServicesMap,
  serviceRoleMap => {
    return serviceRoleMap.map(({ role }) => role);
  },
);
